<template>
  <div class="product-detail">
    <!-- 产品基本信息 -->
    <div class="product-header">
      <div class="product-info">
        <h1>{{ product.name }}</h1>
        <p class="description">{{ product.fullDescription }}</p>
      </div>
      <div class="product-image">
        <el-image 
          :src="product.image" 
          fit="contain"
          :preview-src-list="[product.image]"
          :initial-index="0"
          preview-teleported
        />
      </div>
    </div>

    <!-- 产品详细信息 -->
    <div class="product-content">
      <div class="content-section">
        <h2 class="section-title">技术规格</h2>
        <div class="specifications-section">
          <!-- 聚焦毛细管规格表（原产品1，新产品可套用） -->
          <template v-if="specView === 'focus'">
            <div class="spec-table" v-if="product.specifications">
              <el-table :data="product.specifications" border>
                <el-table-column prop="type" label="型号" />
                <el-table-column prop="rms" label="全宽均方根 @ MoK" />
                <el-table-column prop="energyRange" label="能量范围(keV)" />
                <el-table-column prop="focusDistance" label="聚焦距离" />
              </el-table>
            </div>
          </template>
          <!-- 准直毛细管规格表（原产品2，新产品可套用） -->
          <template v-else-if="specView === 'collimate'">
            <div class="spec-table" v-if="product.specifications">
              <el-table :data="product.specifications" border>
                <el-table-column prop="type" label="型号" />
                <el-table-column prop="outputDiameter" label="输出直径 Dout [mm]" />
                <el-table-column prop="outputDivergence" label="输出发散 Δθ [° (CuKα)]" />
                <el-table-column prop="transmissionCoefficient" label="透射系数 KTr [% (CuKα)]" />
              </el-table>
            </div>
          </template>
          <!-- 导光板规格表（原产品3） -->
          <template v-else-if="specView === 'param-guide'">
            <div class="spec-table" v-if="product.specifications">
              <el-table :data="product.specifications" border>
                <el-table-column prop="parameter" label="技术规格" />
                <el-table-column prop="value" label="参数值" />
              </el-table>
            </div>
          </template>
          <!-- 产品4的规格说明 -->
          <template v-else-if="specView === 'text'">
            <div class="spec-description">
              <p>{{ product.technicalDetails || '暂无详细规格' }}</p>
            </div>
          </template>
          <!-- 新增：光电二极管 (ID 5, 6, 7) 规格表 -->
          <template v-else-if="specView === 'photodiode'">
            <!-- 桌面端表格 -->
            <div class="spec-table" v-if="!isMobile && product.specifications && product.specifications.length">
              <el-table :data="product.specifications" border stripe style="width: 100%">
                <el-table-column prop="partNumber" label="型号 (Part Number)" min-width="180" align="center" />
                <el-table-column prop="sensitiveArea" label="敏感面积 (mm²)" width="160" align="center" />
                <el-table-column prop="size" label="尺寸 (mm)" width="160" align="center" />
                <el-table-column prop="shuntResistance" label="分流电阻 (MΩ)" width="160" align="center" />
                <el-table-column prop="capacitance" label="电容 @ 0V (nF)" width="160" align="center" />
                <el-table-column prop="riseTime" label="上升时间 (10-90%)" width="180" align="center" />
                <!-- AXUV 特有的列 -->
                <el-table-column v-if="showPhotodiodeExtra" prop="subType" label="类型 (Type)" min-width="200" /> 
                <el-table-column v-if="showPhotodiodeExtra" prop="darkCurrent" label="暗电流 @ 50V (pA)" width="180" align="center" />
                <!-- 图片列 -->
                <el-table-column label="图片/资料" width="150" align="center">
                  <template #default="scope">
                    <el-image 
                      v-if="scope.row.imagePath"
                      :src="scope.row.imagePath" 
                      fit="contain"
                      style="width: 100px; height: 40px; vertical-align: middle;" 
                      :preview-src-list="[scope.row.imagePath]"
                      :initial-index="0"
                      preview-teleported
                      lazy
                    >
                      <template #error>
                        <div class="image-slot" style="height: 40px;">暂无图片</div>
                      </template>
                       <template #placeholder>
                        <div class="image-slot" style="height: 40px;">加载中...</div>
                      </template>
                    </el-image>
                    <span v-else>N/A</span>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <!-- 移动端卡片列表 -->
            <div class="mobile-specs-list" v-else-if="isMobile && product.specifications && product.specifications.length">
              <div class="mobile-spec-card" v-for="(spec, index) in product.specifications" :key="index">
                <div class="mobile-spec-header">
                  <h4>{{ spec.partNumber }}</h4>
                  <span v-if="showPhotodiodeExtra && spec.subType" class="spec-subtype">({{ spec.subType }})</span>
                </div>
                <div class="mobile-spec-content">
                  <div class="mobile-spec-image">
                     <el-image 
                      v-if="spec.imagePath"
                      :src="spec.imagePath" 
                      fit="contain"
                      style="width: 100%; height: 100px;" 
                      :preview-src-list="[spec.imagePath]"
                      :initial-index="0"
                      preview-teleported
                    >
                      <template #error>
                        <div class="image-slot">暂无图片</div>
                      </template>
                    </el-image>
                     <div v-else class="image-slot" style="height: 100px;">暂无图片</div>
                  </div>
                  <div class="mobile-spec-details">
                    <p><strong>敏感区域:</strong> {{ spec.sensitiveArea || 'N/A' }} mm²</p>
                    <p><strong>尺寸:</strong> {{ spec.size || 'N/A' }} mm</p>
                    <p><strong>分流电阻:</strong> {{ spec.shuntResistance || 'N/A' }} MΩ</p>
                    <p><strong>电容(@0V):</strong> {{ spec.capacitance || 'N/A' }} nF</p>
                    <p><strong>上升时间:</strong> {{ spec.riseTime || 'N/A' }}</p>
                    <p v-if="showPhotodiodeExtra && spec.darkCurrent"><strong>暗电流(@50V):</strong> {{ spec.darkCurrent }} pA</p>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="spec-description">
              <p>暂无详细规格</p>
            </div>
          </template>
          <!-- 新增：光谱仪 (ID 8) 详情 -->
          <template v-else-if="specView === 'spectrometer'">
            <div class="spec-description">
              <h4>主要特点与规格</h4>
              <p>{{ product.technicalDetails }}</p>
            </div>
             <div class="kit-info-section">
               <h4>评估套件与软件</h4>
               <p>{{ product.kitInfo }}</p>
               <!-- 添加评估套件图片 -->
               <el-image 
                 v-if="product.kitImagePath"
                 :src="product.kitImagePath" 
                 fit="contain"
                 style="max-width: 400px; margin-top: 15px; border-radius: 4px;" 
                 :preview-src-list="[product.kitImagePath]"
                 :initial-index="0"
                 preview-teleported
                 lazy
               >
                  <template #error>
                    <div class="image-slot" style="height: 100px; width: 100px;">图片加载失败</div>
                  </template>
                  <template #placeholder>
                    <div class="image-slot" style="height: 100px; width: 100px;">加载中...</div>
                  </template>
               </el-image>
            </div>
          </template>
          <!-- 调整：射线管 (ID 9, 10) 详情 -->
          <template v-else-if="specView === 'xray'">
            <div class="product-section features-section">
              <h3 class="sub-section-title">主要特点 (Features)</h3>
              <ul>
                <li v-for="(feature, index) in product.features" :key="index">{{ feature }}</li>
              </ul>
            </div>

            <div class="product-section applications-section-grid">
              <h3 class="sub-section-title">应用领域 (Application)</h3>
              <div class="app-grid">
                <div v-for="(appList, appCategory) in product.applications" :key="appCategory" class="app-category">
                  <h4>{{ appCategory }}</h4>
                  <ul>
                    <li v-for="(app, idx) in appList" :key="idx">{{ app }}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="product-section spec-table-section">
               <h3 class="sub-section-title">技术规格 (Specification)</h3>
              <el-table :data="product.specifications" border stripe style="width: 100%">
                <el-table-column prop="parameter" label="参数" width="200"></el-table-column>
                <el-table-column label="描述 / 值">
                  <template #default="scope">
                    <ul v-if="Array.isArray(scope.row.value)" class="spec-value-list">
                      <li v-for="(val, i) in scope.row.value" :key="i">{{ val }}</li>
                    </ul>
                    <span v-else>{{ scope.row.value }}</span>
                  </template>
                </el-table-column>
              </el-table>
              <p v-if="product.disclaimer" class="disclaimer-text">{{ product.disclaimer }}</p>
            </div>

            <div v-if="product.dimensionImagePath" class="product-section dimension-image-section">
              <h3 class="sub-section-title">三维参数图</h3>
              <el-image 
                :src="product.dimensionImagePath"
                fit="contain"
                style="max-width: 600px; display: block; margin: 20px auto; border: 1px solid #eee;" 
                :preview-src-list="[product.dimensionImagePath]"
                :initial-index="0"
                preview-teleported
                lazy
              >
                 <template #error>
                   <div class="image-slot">图片加载失败</div>
                 </template>
                 <template #placeholder>
                   <div class="image-slot">加载中...</div>
                 </template>
              </el-image>
            </div>
          </template>

          <!-- 新增：UV位置传感器 (ID 11-24) 规格表 -->
          <template v-else-if="specView === 'param'">
            <div class="spec-table" v-if="product.specifications && product.specifications.length">
              <el-table :data="product.specifications" border stripe style="width: 100%">
                <el-table-column prop="parameter" label="参数" width="200" align="center" />
                <el-table-column prop="value" label="规格值" min-width="300" align="center">
                  <template #default="scope">
                    <ul v-if="Array.isArray(scope.row.value)" class="spec-value-list">
                      <li v-for="(val, i) in scope.row.value" :key="i">{{ val }}</li>
                    </ul>
                    <span v-else>{{ scope.row.value }}</span>
                  </template>
                </el-table-column>
              </el-table>
            </div>
            <div v-else class="spec-description">
              <p>暂无详细规格信息</p>
            </div>
          </template>

          <template v-else-if="product.specifications && product.specifications.length">
            <div class="spec-table">
              <el-table :data="product.specifications" border stripe style="width: 100%">
                <el-table-column prop="parameter" label="参数" width="200" align="center" />
                <el-table-column prop="value" label="规格值" min-width="300" align="center" />
              </el-table>
            </div>
          </template>

          <div v-if="product.content && String(product.content).includes('<')" class="spec-description" v-html="product.content"></div>

          <div v-if="specView === 'empty'" class="spec-description">
             <p>暂无详细规格信息</p>
           </div>
        </div>

        <h2 class="section-title">应用场景</h2>
        <div class="applications-section">
          <div class="application-content">
            <div class="application-text">
              <p>{{ product.applicationDescription }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 相关产品推荐 -->
    <div class="related-products">
      <h2>相关产品推荐</h2>
      <div class="products-grid">
        <div 
          v-for="relatedProduct in relatedProducts" 
          :key="relatedProduct.id"
          class="product-card"
          @click="navigateToProduct(relatedProduct.id)"
        >
          <div class="product-image">
            <el-image 
              :src="relatedProduct.image" 
              fit="contain"
              :preview-src-list="[relatedProduct.image]"
            />
          </div>
          <div class="product-info">
            <h3>{{ relatedProduct.name }}</h3>
            <p>{{ relatedProduct.shortDescription }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchPublishedProducts, fetchProductById, mergeProducts, mapProduct, detectSpecView } from '@/utils/cms'

export default {
  name: 'ProductDetail',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const activeTab = ref('specifications')
    const product = ref({})
    const relatedProducts = ref([])

    // 判断是否为移动端
    const isMobile = computed(() => {
      if (typeof window !== 'undefined') {
        return window.innerWidth <= 768
      }
      return false 
    })
    const specView = computed(() => detectSpecView(product.value))
    const showPhotodiodeExtra = computed(() => {
      const current = product.value || {}
      if (Number(current.id) === 5) return true
      return (current.specifications || []).some(row => row && (row.subType || row.darkCurrent))
    })

    // 产品数据
    const fallbackProducts = [
      {
        id: 1,
        categoryName: 'X射线光学',
        name: '聚焦多毛细管光学元件',
        shortDescription: '聚焦 X 射线可实现小焦点和高强度',
        description: '聚焦 X 射线可实现小焦点和高强度。',
        fullDescription: '多毛细管聚焦光学镜组可聚焦 X 射线，在样品上形成小焦点，实现高强度。根据应用目标，可对光学元件的各个参数进行专门优化。对于各种 X 射线光谱应用，可以使用所谓的全聚毛细管透镜或半透镜。聚焦半透镜可用于同步加速器应用或用于三维光谱分析的共焦 XRF 设备。',
        image: '/images/product1.png',
        applicationDescription: '聚焦半透镜可用于同步加速器应用或用于三维光谱分析的共焦 XRF 设备。根据应用目标，可对光学元件的各个参数进行专门优化，适用于各种 X 射线光谱应用。',
        specifications: [
          { type: '微型透镜', rms: '< 10 µm - 4 mm', energyRange: '1 - 30', focusDistance: '2 毫米 - 2 米' },
          { type: '微透镜', rms: '< 10 微米 - 1 毫米', energyRange: '1 - 30', focusDistance: '2 毫米 - 200 毫米' },
          { type: '聚焦半透镜', rms: '< 10 微米 - 1 毫米', energyRange: '1 - 30', focusDistance: '2 毫米 - 200 毫米' }
        ]
      },
      {
        id: 2,
        categoryName: 'X射线光学',
        name: '准直多毛细光学元件',
        shortDescription: '从发散输入光束到准平行输出光束',
        description: '准直多毛细管光学。从发散输入光束到准平行输出光束。',
        fullDescription: '准直多毛细管光学技术使发散辐射平行化和准直化成为可能。发散输入光束被转化为准平行输出光束。输出光束的发散度较低。这种准直多毛细管光学器件主要应用于 X 射线衍射测量。',
        image: '/images/product2.png',
        applicationDescription: '这种准直多毛细管光学器件主要应用于 X 射线衍射测量。通过将发散输入光束转化为准平行输出光束，实现高效的衍射分析和材料表征。',
        specifications: [
          { type: '大半透镜', outputDiameter: '8 - 16', outputDivergence: '0.2 - 0.3', transmissionCoefficient: '> 40' },
          { type: '微型半透镜', outputDiameter: '5 - 8', outputDivergence: '0.2 - 0.3', transmissionCoefficient: '> 40' },
          { type: '微型半透镜', outputDiameter: '3 - 5', outputDivergence: '0.2 - 0.3', transmissionCoefficient: '> 40' }
        ]
      },
      {
        id: 3,
        categoryName: 'X射线光学',
        name: 'X 射线导光板',
        shortDescription: '有效传输和准直发散的 X 射线',
        description: 'X 射线导光板。有效传输和准直发散的 X 射线',
        fullDescription: '最简单的毛细管光学技术是 X 射线导光板，它是一个圆柱形的单毛细管。它能在空间上准直 X 射线光束，并限制光束出口处的发散角。与针孔孔径相比，X 射线导光板可显著提高样品上的光强。根据不同的应用目标，X 射线导光板可制成不同的直径和长度。',
        image: '/images/product3.png',
        applicationDescription: '与针孔孔径相比，X 射线导光板可显著提高样品上的光强。根据不同的应用目标，可以选择不同直径和长度的导光板，适用于实验室X射线源的光束整形和传输，以及医学诊断成像等领域。',
        specifications: [
          { parameter: '毛细管直径 D1', value: '10 - 1,000 微米' },
          { parameter: '长度 L', value: '135、165、191、210、230 毫米' },
          { parameter: '能量范围', value: '1 - 30 千伏' },
          { parameter: '强度增益', value: '3 - 10' }
        ]
      },
      {
        id: 4,
        categoryName: 'X射线光学',
        name: '成像光学元件',
        shortDescription: '用于 X 射线光束整形的高精度定制光学镜组',
        description: '用于 X 射线光束整形的高精度定制光学镜组和光学元件。',
        fullDescription: '用于 X 射线光束整形的高精度定制光学镜组和光学元件。基于 X 射线在玻璃毛细管内表面上的外部全反射效应。',
        technicalDetails: '暂无',
        applicationDescription: 'X 射线毛细管光学系统的工作模式是基于 X 射线在非常光滑的玻璃毛细管内表面上的外部全反射效应。在入射角度很小的情况下，几乎 100% 的 X 射线都会被毛细管的玻璃壁反射，因此能以极低的损耗引导 X 射线通过光学器件。这样，X 射线就可以通过毛细管的定向变形进行塑形和聚光。大型多毛细管结构（d ≤ 20 毫米）与 CCD 传感器相结合，可对样品进行能量和空间分辨成像。这样，无需对样品进行烧蚀，就能观察到样品的元素分布。放大率可达 1:1 到 1:10。',
        image: '/images/product4.png'
      },
      // --- 新增光电二极管产品 ---
      {
        id: 5,
        categoryName: '光电二极管',
        name: 'AXUV',
        shortDescription: '高性能光子、电子或X射线测量 (0.0124nm-190nm)',
        description: 'AXUV系列探测器用于从0.0124nm到190nm的光子，电子或x射线的高性能测量，探测能量从100eV到50keV。',
        fullDescription: 'AXUV系列探测器具有从0.0124nm到190nm的光子，电子或x射线的高性能测量，探测能量从100eV到50keV，具有显着的100%内部量子效率。',
        image: '/images/AXUV.png', // 确保路径正确
        applicationDescription: '适用于需要高灵敏度和宽动态范围的X射线检测、电子束检测、同步辐射测量以及空间科学等应用。',
        specifications: [
          { subType: 'Absolute Devices/Transfer Standards', partNumber: 'AXUV576C', sensitiveArea: '576.5', size: '24.01 x 24.01', shuntResistance: '50', capacitance: '120', riseTime: '-', imagePath: '/images/AXUV576C.png' },
          { subType: 'Absolute Devices/Transfer Standards', partNumber: 'AXUV100TF400', sensitiveArea: '100', size: '10 x 10', shuntResistance: '20', capacitance: '10', riseTime: '-', imagePath: '/images/AXUV100TF400.png' },
          { subType: 'Absolute Devices/Transfer Standards', partNumber: 'AXUV100TF030', sensitiveArea: '100', size: '10 x 10', shuntResistance: '20', capacitance: '10', riseTime: '100 μsec', imagePath: '/images/AXUV100TF030.png' },
          { subType: 'Absolute Devices/Transfer Standards', partNumber: 'AXUV100G', sensitiveArea: '100', size: '10 x 10', shuntResistance: '20', capacitance: '10', riseTime: '10 μsec', imagePath: '/images/AXUV100G.png' },
          { subType: 'High Speed / Low Capacitance', partNumber: 'AXUVPS7', sensitiveArea: '15', size: '0.15Ø', darkCurrent: '10', capacitance: '1.5', riseTime: '2000 psec', imagePath: '/images/AXUVPS7.png' },
          { subType: 'High Speed / Low Capacitance', partNumber: 'AXUV63HS1', sensitiveArea: '63', size: '9 Ø', darkCurrent: '100', capacitance: '10', riseTime: '2000 psec', imagePath: '/images/AXUV63HS1.png' },
          { subType: 'High Speed / Low Capacitance', partNumber: 'AXUV20HS1', sensitiveArea: '20', size: '5.01 Φ', darkCurrent: '100', capacitance: '300', riseTime: '3500 psec', imagePath: '/images/AXUV20HS1.png' },
          { subType: 'ELG Series', partNumber: 'AXUV16ELG', sensitiveArea: '10', size: '2 x 5', shuntResistance: '100', capacitance: '40', riseTime: '500 nsec', imagePath: '/images/AXUV16ELG.png' },
        ]
      },
      {
        id: 6,
        categoryName: '光电二极管',
        name: 'SXUV',
        shortDescription: 'EUV优化光电二极管 (1-190nm)，适用于13.5nm光刻',
        description: 'euv优化的光电二极管（1-190 nm）具有稳定的响应性，适合13.5 nm光刻和极端紫外线应用。',
        fullDescription: 'Opto Diode的SXUV系列光电二极管专为精确检测极紫外（EUV）辐射，x射线和电子而设计。这些探测器具有20 mm²的圆形有源面积，在1 nm至200 nm波长范围内提供卓越的响应性，使其成为高强度EUV应用的理想选择。',
        image: '/images/SXUV.png', // 确保路径正确
        applicationDescription: '理想用于13.5nm光刻监控、EUV源标定、X射线剂量测定以及其他需要精确测量短波长辐射的场合。',
        specifications: [
          { partNumber: 'SXUV20HS1', sensitiveArea: '19.7', size: '5.01Ø', shuntResistance: '-', capacitance: '200', riseTime: '2 μsec', imagePath: '/images/SXUV20HS1.png' },
          { partNumber: 'SXUV100', sensitiveArea: '100', size: '10 x 10', shuntResistance: '20', capacitance: '15', riseTime: '6 μsec', imagePath: '/images/SXUV100.png' },
          { partNumber: 'SXUV300C', sensitiveArea: '331', size: '22.05 x 15.85', shuntResistance: '5', capacitance: '40', riseTime: '6 μsec', imagePath: '/images/SXUV300C.png' },
          { partNumber: 'SXUVPS4C', sensitiveArea: '5', size: '2.5Ø', shuntResistance: '100', capacitance: '0.5', riseTime: '1 μsec', imagePath: '/images/SXUVPS4C.png' },
          { partNumber: 'SXUVPS4', sensitiveArea: '1.25', size: 'R.049 [1.25 mm]', shuntResistance: '100', capacitance: '500', riseTime: '-', imagePath: '/images/SXUVPS4.png' },
          { partNumber: 'SXUV5', sensitiveArea: '5', size: '2.5 diameter', shuntResistance: '20', capacitance: '500', riseTime: '1 μsec', imagePath: '/images/SXUV5.png' }
        ]
      },
      {
        id: 7,
        categoryName: '光电二极管',
        name: 'UVG',
        shortDescription: '可靠的紫外探测器 (190-400nm)，用于UV-A/B/C监测',
        description: '可靠的紫外探测器（190-400 nm），响应稳定，专为精确的UV- a， UV- b和UV- c监测而设计。',
        fullDescription: 'Opto Diode的UVG系列光电二极管专为高灵敏度检测深紫外（DUV）和极紫外（EUV）辐射而设计。这些探测器在190 nm至400 nm范围内具有卓越的响应性，为苛刻的科学，工业和航空航天应用提供精度和稳定性。UVG光电二极管专为低暗电流和高量子效率而设计，在需要精确紫外测量的环境中提供可靠的性能。',
        image: '/images/UVG.png', // 更新主图路径
        applicationDescription: '广泛应用于水净化系统的UV剂量监测、UV固化过程控制、日光紫外线指数测量、医疗光疗设备以及航空航天紫外辐射探测等。',
        specifications: [
          { partNumber: 'UVG100', sensitiveArea: '100', size: '10 x 10', shuntResistance: '100', capacitance: '20', riseTime: '10 μsec', imagePath: '/images/UVG100.png' },
          { partNumber: 'UVG12', sensitiveArea: '13.2', size: '4.1Φ', shuntResistance: '1000', capacitance: '3', riseTime: '4 μsec', imagePath: '/images/UVG12.png' },
          { partNumber: 'UVG20C', sensitiveArea: '24', size: '5.5 Ø', shuntResistance: '100', capacitance: '1.2', riseTime: '1 μsec', imagePath: '/images/UVG20C.png' },
          { partNumber: 'UVG20S', sensitiveArea: '24', size: '5.5 Ø', shuntResistance: '100', capacitance: '1.2', riseTime: '1 μsec', imagePath: '/images/UVG20S.png' },
          { partNumber: 'UVG5S', sensitiveArea: '5', size: '2.5 Ø', shuntResistance: '-', capacitance: '500', riseTime: '1 μsec', imagePath: '/images/UVG5S.png' }
        ]
      },
      // --- 新增光谱仪产品 ---
      {
        id: 8,
        categoryName: '微型傅里叶变换光谱仪',
        name: 'NIRONE Sensor S',
        shortDescription: '小巧、便携、坚固且经济实惠的近红外光谱传感器',
        description: 'NIRONE Sensor S 将材料测量从实验室带到了现场，能够在近红外 (NIR) 光谱范围（1350 至 2450 nm）内进行快速、可靠的测量。',
        fullDescription: '它小巧、便携、创新、坚固且经济实惠。它能够在近红外 (NIR) 光谱范围（1350 至 2450 nm）内进行快速、可靠的测量，即使在最苛刻的环境中也能正常工作。NIRONE Sensor S 以小巧的体积提供了出色的表现，完全可与最优质的实验室仪器相媲美，而成本只是其中的一小部分。',
        image: '/images/SensorS.png', // 更新主图路径
        technicalDetails: '光谱范围：1350 - 2450 nm。特点：小巧、便携、创新、坚固、经济实惠、快速可靠测量。',
        applicationDescription: '它可以通过近红外光谱技术为材料分析创造新的应用，包括：\n• 药品成分分析\n• 品牌产品验证（anti-counterfeiting）\n• 纺织品和塑料的识别\n• 法医应用，例如检测毒品和爆炸物\n• 湿度分析\n• 农业应用，例如土壤、谷物、饲料和乳制品的分析',
        kitInfo: 'NIRONE 传感器评估套件为技术评估和应用研究提供了一个良好的起点。评估套件包括一个USB通信板，传感器可以通过我们用户友好的SensorControl软件在PC上进行控制。评估套件是立即开始您的NIR应用研究的最简单和最快速的方法。我们的SensorControl软件可以免费与我们的任何设备一起使用。', // 保持文本信息
        kitImagePath: '/images/NIRONE传感器评估套件.png', // 新增字段存储套件图片路径
      },
      // --- 新增射线管产品 ---
      {
        id: 9,
        categoryName: '微型一体化射线管',
        name: 'SXF-5010-C [XRF Source]',
        shortDescription: '紧凑、低功耗、易于集成的微型一体化射线管，适用于XRF和X射线成像。',
        description: '紧凑型设计，最小焦斑0.3mm，适用于手持、便携式和台式仪器。功耗低，电池寿命长，可定制配置。',
        fullDescription: 'SXF-5010-C是一款金属陶瓷结构的微型一体化X射线源，采用接地阳极设计。其紧凑、轻便的设计使其非常适合集成到手持、便携式以及台式仪器中。具有低功耗和最小0.3mm焦斑尺寸的特点，为现场XRF材料分析和X射线成像应用提供了可靠、高效的解决方案。',
        image: '/images/SXF5010C.png', // 主图
        dimensionImagePath: '/images/SXF5010CDimension.png', // 三维参数图
        features: [
          '紧凑设计，适用于手持、便携和台式仪器',
          '最小焦斑尺寸 (min 0.3mm) ',
          '低功耗，电池寿命长',
          '可定制配置，灵活订购',
          '重量轻，便于携带和集成'
        ],
        applications: {
          'XRF 材料分析': [
            '合金与金属分选', '环境分析', 'ROHS & ELV', '艺术与考古计量',
            '研发', '质量控制', '涂层厚度', '采矿与地质', '法医科学', '铅检测'
          ],
          'X射线成像': [
            '医疗研发、牙科、小动物成像', '安检、违禁品检查', '射线照相检查', '多层胶片检查'
          ]
        },
        specifications: [
          { parameter: '射线管类型', value: '金属陶瓷结构，接地阳极' },
          { parameter: '靶材', value: '银 (Ag) 透射型' },
          { parameter: '窗口', value: '铍 (Be) 127μm' },
          { parameter: '出束锥角', value: '60° (带 Ø3mm 准直器)' },
          { parameter: '焦斑尺寸', value: '小于 1mm' },
          { parameter: '管电压', value: '0 至 -50kV' },
          { parameter: '管电流', value: '0 至 200μA' },
          { parameter: '输入电压', value: '9 至 12VDC' },
          { parameter: '工作温度', value: '-10°C 至 50°C' },
          { parameter: '存储温度', value: '-40°C 至 85°C' },
          { parameter: '接口信号', value: [
              '输出电压控制: 0-4V = 0-100%',
              '输出电压监测: 0-4V = 0-100%',
              '输出电流控制: 0-4V = 0-100%',
              '输出电流监测: 0-4V = 0-100%',
              '使能: +5VDC = ON, <1VDC = OFF'
            ]
          }
        ],
        disclaimer: '※ 产品外观和规格可能会因产品改进而更改，恕不另行通知。'
      },
      // --- 新增第二个射线管产品 ---
      {
        id: 10,
        categoryName: '微型一体化射线管',
        name: 'SL Series X-ray Source', // 暂定名，可修改
        shortDescription: '紧凑、低功耗、易于集成的微型一体化射线管，适用于XRF和X射线成像。',
        description: '紧凑型设计，最小焦斑0.3mm，适用于手持、便携式和台式仪器。功耗低，电池寿命长，可定制配置。',
        fullDescription: 'SL系列同样是一款金属陶瓷结构的微型一体化X射线源，采用接地阳极设计。其紧凑、轻便的设计使其非常适合集成到手持、便携式以及台式仪器中。具有低功耗和最小0.3mm焦斑尺寸的特点，为现场XRF材料分析和X射线成像应用提供了可靠、高效的解决方案。', // 描述微调
        image: '/images/SL.png', // 主图
        dimensionImagePath: '/images/SLD.png', // 三维参数图
        features: [
          '紧凑设计，适用于手持、便携和台式仪器',
          '最小焦斑尺寸 (min 0.3mm) ',
          '低功耗，电池寿命长',
          '可定制配置，灵活订购',
          '重量轻，便于携带和集成'
        ],
        applications: {
          'XRF 材料分析': [
            '合金与金属分选', '环境分析', 'ROHS & ELV', '艺术与考古计量',
            '研发', '质量控制', '涂层厚度', '采矿与地质', '法医科学', '铅检测'
          ],
          'X射线成像': [
            '医疗研发、牙科、小动物成像', '安检、违禁品检查', '射线照相检查', '多层胶片检查'
          ]
        },
        specifications: [
          { parameter: '射线管类型', value: '金属陶瓷结构，接地阳极' },
          { parameter: '靶材', value: '银 (Ag) 透射型' }, // 注意：图片未明确说明，沿用上一个
          { parameter: '窗口', value: '铍 (Be) 127μm' },
          { parameter: '出束锥角', value: '60° (带 Ø3mm 准直器)' },
          { parameter: '焦斑尺寸', value: '小于 1mm' },
          { parameter: '管电压', value: '0 至 -50kV' },
          { parameter: '管电流', value: '0 至 200μA' },
          { parameter: '输入电压', value: '9 至 12VDC' },
          { parameter: '工作温度', value: '-10°C 至 50°C' },
          { parameter: '存储温度', value: '-40°C 至 85°C' },
          { parameter: '接口信号', value: [
              '输出电压控制: 0-4V = 0-100%',
              '输出电压监测: 0-4V = 0-100%',
              '输出电流控制: 0-4V = 0-100%',
              '输出电流监测: 0-4V = 0-100%',
              '使能: +5VDC = ON, <1VDC = OFF'
            ]
          }
        ],
        disclaimer: '※ 产品外观和规格可能会因产品改进而更改，恕不另行通知。'
      },
      // --- 新增UV位置传感器产品 ---
      {
        id: 11,
        categoryName: 'UV位置传感器',
        name: 'S1-0073-1L10UV_CP2',
        shortDescription: '一维UV位置传感器，10×2mm²有效区域，高线性度。',
        description: '一维UV位置传感器，10×2mm²有效区域，高线性度。',
        fullDescription: 'SiTek 1L10UV PSD专为UV波长区域200-400nm优化设计。作为SiTek标准PSD，它按照横向效应光电二极管原理工作。这是一个模拟设备，具有出色的位置分辨率。分辨率由系统信噪比决定。1L10UV在偏置模式下工作。',
        image: '/images/S1-0073-1L10UV_CP2_1.png',
        applicationDescription: '典型应用包括：距离和高度测量、对准、位置和运动测量以及振动研究。该传感器具有高线性度和优异的位置分辨率，适用于需要精确位置检测的UV光学系统。',
        specifications: [
          { parameter: '有效区域', value: '10 × 2 mm²' },
          { parameter: '位置非线性', value: '1-2 %(±)' },
          { parameter: '探测器电阻', value: '40-80 kΩ' },
          { parameter: '暗电流', value: '8-50 nA' },
          { parameter: '噪声电流', value: '0.4-1.0 pA/√Hz' },
          { parameter: '响应度', value: '0.5 A/W' },
          { parameter: '电容', value: '15-20 pF' },
          { parameter: '上升时间(10-90%)', value: '200-400 ns' },
          { parameter: '反向电压(偏置)', value: '5-30 V' },
          { parameter: '热漂移', value: '20-100 ppm/°C' },
          { parameter: '最大反向电压', value: '30 V' },
          { parameter: '工作温度', value: '70 °C' },
          { parameter: '存储温度', value: '100 °C' },
          { parameter: '封装', value: '14针陶瓷DIL封装，19.6 × 7.3 mm²，带保护石英窗口' },
          { parameter: '测试条件', value: '室温23°C，反向电压15V，光源波长940nm' }
        ]
      },
      {
        id: 12,
        categoryName: 'UV位置传感器',
        name: 'S1-0074-1L20UV_CP3',
        shortDescription: '一维UV位置传感器，20×3mm²有效区域，大检测面积。',
        description: '一维UV位置传感器，20×3mm²有效区域，大检测面积。',
        fullDescription: 'SiTek 1L20UV专为UV波长区域200-400nm优化设计。作为SiTek标准PSD，它按照横向效应光电二极管原理工作。这是一个模拟设备，因此具有出色的位置分辨率。分辨率由系统信噪比决定。1L20UV在偏置模式下工作。',
        image: '/images/S1-0074-1L20UV_CP3_2.png',
        applicationDescription: '典型应用包括：距离和高度测量、对准、位置和运动测量以及振动研究。该传感器具有大检测面积和高精度，适用于需要大范围位置检测的UV光学系统。',
        specifications: [
          { parameter: '有效区域', value: '20 × 3 mm²' },
          { parameter: '位置非线性', value: '1-2 %(±)' },
          { parameter: '探测器电阻', value: '40-80 kΩ' },
          { parameter: '暗电流', value: '50-250 nA' },
          { parameter: '噪声电流', value: '0.5-1.0 pA/√Hz' },
          { parameter: '响应度', value: '0.5 A/W' },
          { parameter: '电容', value: '45-55 pF' },
          { parameter: '上升时间(10-90%)', value: '0.5-1.0 μs' },
          { parameter: '反向电压(偏置)', value: '5-30 V' },
          { parameter: '热漂移', value: '20-100 ppm/°C' },
          { parameter: '最大反向电压', value: '30 V' },
          { parameter: '工作温度', value: '70 °C' },
          { parameter: '存储温度', value: '100 °C' },
          { parameter: '封装', value: '22针陶瓷DIL封装，31.8 × 9.9 mm²，带保护石英窗口' },
          { parameter: '测试条件', value: '室温23°C，反向电压15V，光源波长940nm' }
        ]
      },
      {
        id: 13,
        categoryName: 'UV位置传感器',
        name: 'S2-0184-2L4_SU71',
        shortDescription: '二维UV位置传感器，4×4mm²有效区域，SMD封装。',
        description: '二维UV位置传感器，4×4mm²有效区域，SMD封装。',
        fullDescription: 'SiTek 2L4 PSD按照横向效应光电二极管原理工作。这是一个模拟设备，因此具有出色的位置分辨率。分辨率由系统信噪比决定。2L4在偏置模式下工作。提供特殊的UV或YAG增强和核版本。',
        image: '/images/S2-0184-2L4_SU71_1.png',
        applicationDescription: '典型应用包括：距离和高度测量、对准、位置和运动测量以及振动研究。该传感器具有SMD封装和紧凑设计，适用于需要小型化集成的UV光学系统。',
        specifications: [
          { parameter: '有效区域', value: '4 × 4 mm²' },
          { parameter: '位置非线性', value: '0.3-0.8 %(±)' },
          { parameter: '探测器电阻', value: '7-16 kΩ' },
          { parameter: '暗电流', value: '50-200 nA' },
          { parameter: '噪声电流', value: '1.3-2.5 pA/√Hz' },
          { parameter: '响应度', value: '0.63 A/W' },
          { parameter: '电容', value: '20-25 pF' },
          { parameter: '上升时间(10-90%)', value: '80-160 ns' },
          { parameter: '反向电压(偏置)', value: '5-20 V' },
          { parameter: '热漂移', value: '40-200 ppm/°C' },
          { parameter: '最大反向电压', value: '30 V' },
          { parameter: '工作温度', value: '70 °C' },
          { parameter: '存储温度', value: '100 °C' },
          { parameter: '封装', value: 'SMD封装，12.7 × 12.7 mm²，带保护窗口' },
          { parameter: '测试条件', value: '室温23°C，反向电压15V，光源波长940nm' }
        ]
      },
      {
        id: 14,
        categoryName: 'UV位置传感器',
        name: 'S2-0034-2L20UV_SU9',
        shortDescription: '二维UV位置传感器，20×20mm²有效区域，大检测面积。',
        description: '二维UV位置传感器，20×20mm²有效区域，大检测面积。',
        fullDescription: 'SiTek 2L20UV专为UV波长区域200-400nm优化设计。作为SiTek标准PSD，它按照横向效应光电二极管原理工作。这是一个模拟设备，因此具有出色的位置分辨率。分辨率由系统信噪比决定。2L20UV在偏置模式下工作。',
        image: '/images/S2-0034-2L20UV_SU9_1.png',
        applicationDescription: '典型应用包括：距离和高度测量、对准、位置和运动测量以及振动研究。该传感器具有大检测面积和高精度，适用于需要大范围二维位置检测的UV光学系统。',
        specifications: [
          { parameter: '有效区域', value: '20 × 20 mm²' },
          { parameter: '位置非线性', value: '1-2 %(±)' },
          { parameter: '探测器电阻', value: '7-16 kΩ' },
          { parameter: '暗电流', value: '200-2000 nA' },
          { parameter: '噪声电流', value: '1.5-3.5 pA/√Hz' },
          { parameter: '响应度', value: '0.5 A/W' },
          { parameter: '电容', value: '360-430 pF' },
          { parameter: '上升时间(10-90%)', value: '1.6-3.0 μs' },
          { parameter: '反向电压(偏置)', value: '5-30 V' },
          { parameter: '热漂移', value: '40-200 ppm/°C' },
          { parameter: '最大反向电压', value: '30 V' },
          { parameter: '工作温度', value: '70 °C' },
          { parameter: '存储温度', value: '100 °C' },
          { parameter: '封装', value: '陶瓷基板，38.0 × 34.0 mm²，带可焊接引脚和保护石英窗口' },
          { parameter: '测试条件', value: '室温23°C，反向电压15V，光源波长940nm' }
        ]
      },
      {
        id: 15,
        categoryName: 'UV位置传感器',
        name: 'S2-0030-2L2UV_MP1',
        shortDescription: '二维UV位置传感器，2×2mm²有效区域，快速响应。',
        description: '二维UV位置传感器，2×2mm²有效区域，快速响应。',
        fullDescription: 'SiTek 2L2UV专为UV波长区域200-400nm优化设计。作为SiTek标准PSD，它按照横向效应光电二极管原理工作。这是一个模拟设备，因此具有出色的位置分辨率。分辨率由系统信噪比决定。2L2UV在偏置模式下工作。',
        image: '/images/S2-0030-2L2UV_MP1_1.png',
        applicationDescription: '典型应用包括：距离和高度测量、对准、位置和运动测量以及振动研究。该传感器具有小检测面积和快速响应，适用于需要高速位置检测的UV光学系统。',
        specifications: [
          { parameter: '有效区域', value: '2 × 2 mm²' },
          { parameter: '位置非线性', value: '1-2 %(±)' },
          { parameter: '探测器电阻', value: '7-16 kΩ' },
          { parameter: '暗电流', value: '50-200 nA' },
          { parameter: '噪声电流', value: '1.3-2.5 pA/√Hz' },
          { parameter: '响应度', value: '0.46 A/W' },
          { parameter: '电容', value: '7-8 pF' },
          { parameter: '上升时间(10-90%)', value: '30-60 ns' },
          { parameter: '反向电压(偏置)', value: '5-30 V' },
          { parameter: '热漂移', value: '40-200 ppm/°C' },
          { parameter: '最大反向电压', value: '30 V' },
          { parameter: '工作温度', value: '70 °C' },
          { parameter: '存储温度', value: '100 °C' },
          { parameter: '封装', value: '12针TO-8金属外壳，15.3mm直径，带保护蓝宝石窗口' },
          { parameter: '测试条件', value: '室温23°C，反向电压15V，光源波长940nm' }
        ]
      },
      {
        id: 16,
        categoryName: 'UV位置传感器',
        name: 'S2-0016_2L10UV_SU7',
        shortDescription: '二维UV位置传感器，10×10mm²有效区域，陶瓷基板。',
        description: '二维UV位置传感器，10×10mm²有效区域，陶瓷基板。',
        fullDescription: 'SiTek 2L10UV专为UV波长区域200-400nm优化设计。作为SiTek标准PSD，它按照横向效应光电二极管原理工作。这是一个模拟设备，因此具有出色的位置分辨率。分辨率由系统信噪比决定。2L10UV在偏置模式下工作。',
        image: '/images/S2-0016_2L10UV_SU7_1.png',
        applicationDescription: '典型应用包括：距离和高度测量、对准、位置和运动测量以及振动研究。该传感器具有陶瓷基板和中等检测面积，适用于需要稳定性和精度的UV光学系统。',
        specifications: [
          { parameter: '有效区域', value: '10 × 10 mm²' },
          { parameter: '位置非线性', value: '1-2 %(±)' },
          { parameter: '探测器电阻', value: '7-16 kΩ' },
          { parameter: '暗电流', value: '100-500 nA' },
          { parameter: '噪声电流', value: '1.3-2.5 pA/√Hz' },
          { parameter: '响应度', value: '0.5 A/W' },
          { parameter: '电容', value: '90-110 pF' },
          { parameter: '上升时间(10-90%)', value: '400-800 ns' },
          { parameter: '反向电压(偏置)', value: '5-30 V' },
          { parameter: '热漂移', value: '40-200 ppm/°C' },
          { parameter: '最大反向电压', value: '30 V' },
          { parameter: '工作温度', value: '70 °C' },
          { parameter: '存储温度', value: '100 °C' },
          { parameter: '封装', value: '陶瓷基板，25.0 × 21.0 mm²，带可焊接引脚和保护石英窗口' },
          { parameter: '测试条件', value: '室温23°C，反向电压15V，光源波长940nm' }
        ]
      },
      {
        id: 17,
        categoryName: 'UV位置传感器',
        name: 'S2-0006-2L4UV_MP1',
        shortDescription: '二维UV位置传感器，4×4mm²有效区域，TO-8封装。',
        description: '二维UV位置传感器，4×4mm²有效区域，TO-8封装。',
        fullDescription: 'SiTek 2L4UV专为UV波长区域200-400nm优化设计。作为SiTek标准PSD，它按照横向效应光电二极管原理工作。这是一个模拟设备，因此具有出色的位置分辨率。分辨率由系统信噪比决定。2L4UV在偏置模式下工作。',
        image: '/images/S2-0006-2L4UV_MP1_1.png',
        applicationDescription: '典型应用包括：距离和高度测量、对准、位置和运动测量以及振动研究。该传感器具有TO-8金属外壳封装，适用于需要机械稳定性的UV光学系统。',
        specifications: [
          { parameter: '有效区域', value: '4 × 4 mm²' },
          { parameter: '位置非线性', value: '1-2 %(±)' },
          { parameter: '探测器电阻', value: '7-16 kΩ' },
          { parameter: '暗电流', value: '50-200 nA' },
          { parameter: '噪声电流', value: '1.3-2.5 pA/√Hz' },
          { parameter: '响应度', value: '0.46 A/W' },
          { parameter: '电容', value: '20-25 pF' },
          { parameter: '上升时间(10-90%)', value: '80-160 ns' },
          { parameter: '反向电压(偏置)', value: '5-30 V' },
          { parameter: '热漂移', value: '40-200 ppm/°C' },
          { parameter: '最大反向电压', value: '30 V' },
          { parameter: '工作温度', value: '70 °C' },
          { parameter: '存储温度', value: '100 °C' },
          { parameter: '封装', value: '12针TO-8金属外壳，15.3mm直径，带保护蓝宝石窗口' },
          { parameter: '测试条件', value: '室温23°C，反向电压15V，光源波长940nm' }
        ]
      },
      {
        id: 18,
        categoryName: 'UV位置传感器',
        name: 'S1-0356-D_A',
        shortDescription: 'PSD带外壳UV探测器，10×2mm²有效区域，DSUB9连接器。',
        description: 'PSD带外壳UV探测器，10×2mm²有效区域，DSUB9连接器。',
        fullDescription: 'S1-0356-D_A是一款带外壳的PSD UV探测器，具有10×2mm²的有效区域。该产品采用14针DIL封装，带有石英窗口，封装在黑色阳极氧化铝外壳中，尺寸为52×52mm²，配备DSUB9连接器。',
        image: '/images/S1-0356-K_A.png',
        applicationDescription: '该探测器适用于需要高精度位置检测的UV光学系统，外壳设计提供了良好的机械保护和热管理，DSUB9连接器便于系统集成。',
        specifications: [
          { parameter: '有效区域', value: '10.0 × 2.0 mm²' },
          { parameter: '芯片区域', value: '12.2 × 3.0 mm²' },
          { parameter: '位置非线性', value: '1-2 %(±)' },
          { parameter: '探测器电阻', value: '40-80 kΩ' },
          { parameter: '漏电流', value: '8-50 nA' },
          { parameter: '噪声电流', value: '0.4-1.0 pA/√Hz' },
          { parameter: '响应度', value: '0.5 A/W' },
          { parameter: '电容', value: '15-20 pF' },
          { parameter: '上升时间(10%-90%)', value: '200-400 ns' },
          { parameter: '偏置电压(反向)', value: '5-20 V' },
          { parameter: '热漂移', value: '20-100 ppm/°C' },
          { parameter: '最大反向电压', value: '30 V' },
          { parameter: '工作温度', value: '70 °C' },
          { parameter: '存储温度', value: '100 °C' },
          { parameter: '封装', value: '14针DIL封装，19.6 × 7.6 mm²，带石英窗口，封装在黑色阳极氧化铝外壳中，52 × 52 mm²，配备DSUB9连接器' },
          { parameter: '测试条件', value: '室温23°C，偏置电压15V，光源波长940nm' }
        ]
      },
      {
        id: 19,
        categoryName: 'UV位置传感器',
        name: 'S1-0356-K_A',
        shortDescription: 'PSD带外壳UV探测器，10×2mm²有效区域，专业封装。',
        description: 'PSD带外壳UV探测器，10×2mm²有效区域，专业封装。',
        fullDescription: 'S1-0356-K_A是一款带外壳的PSD UV探测器，具有10×2mm²的有效区域。该产品采用专业封装设计，提供优异的性能和可靠性。',
        image: '/images/S1-0356-K_A.png',
        applicationDescription: '该探测器适用于需要高精度位置检测的UV光学系统，专业封装设计确保了长期稳定性和优异的环境适应性。',
        specifications: [
          { parameter: '有效区域', value: '10.0 × 2.0 mm²' },
          { parameter: '芯片区域', value: '12.2 × 3.0 mm²' },
          { parameter: '位置非线性', value: '1-2 %(±)' },
          { parameter: '探测器电阻', value: '40-80 kΩ' },
          { parameter: '漏电流', value: '8-50 nA' },
          { parameter: '噪声电流', value: '0.4-1.0 pA/√Hz' },
          { parameter: '响应度', value: '0.5 A/W' },
          { parameter: '电容', value: '15-20 pF' },
          { parameter: '上升时间(10%-90%)', value: '200-400 ns' },
          { parameter: '偏置电压(反向)', value: '5-20 V' },
          { parameter: '热漂移', value: '20-100 ppm/°C' },
          { parameter: '最大反向电压', value: '30 V' },
          { parameter: '工作温度', value: '70 °C' },
          { parameter: '存储温度', value: '100 °C' },
          { parameter: '封装', value: '14针DIL封装，19.6 × 7.6 mm²，带石英窗口，封装在黑色阳极氧化铝外壳中，52 × 52 mm²，配备DSUB9连接器' },
          { parameter: '测试条件', value: '室温23°C，偏置电压15V，光源波长940nm' }
        ]
      },
      {
        id: 20,
        categoryName: 'UV位置传感器',
        name: 'S2-0385-D',
        shortDescription: 'PSD带外壳UV探测器，4×4mm²有效区域，PGA陶瓷封装。',
        description: 'PSD带外壳UV探测器，4×4mm²有效区域，PGA陶瓷封装。',
        fullDescription: 'S2-0385-D是一款带外壳的PSD UV探测器，具有4×4mm²的有效区域。该产品采用4针PGA陶瓷封装，带有石英窗口，封装在黑色阳极氧化铝外壳中，尺寸为52×52mm²，配备DSUB9连接器。',
        image: '/images/S2-0385-K.png',
        applicationDescription: '该探测器适用于需要高精度二维位置检测的UV光学系统，PGA陶瓷封装提供了优异的性能和可靠性，外壳设计确保了良好的机械保护。',
        specifications: [
          { parameter: '有效区域', value: '4.0 × 4.0 mm²' },
          { parameter: '芯片区域', value: '6.4 × 6.4 mm²' },
          { parameter: '位置非线性', value: '1-2 %(±)' },
          { parameter: '探测器电阻', value: '7-16 kΩ' },
          { parameter: '漏电流', value: '50-200 nA' },
          { parameter: '噪声电流', value: '1.3-2.5 pA/√Hz' },
          { parameter: '响应度', value: '0.46 A/W' },
          { parameter: '电容', value: '20-25 pF' },
          { parameter: '上升时间(10%-90%)', value: '80-160 ns' },
          { parameter: '偏置电压(反向)', value: '5-30 V' },
          { parameter: '热漂移', value: '40-200 ppm/°C' },
          { parameter: '最大反向电压', value: '30 V' },
          { parameter: '工作温度', value: '70 °C' },
          { parameter: '存储温度', value: '100 °C' },
          { parameter: '封装', value: '4针PGA陶瓷封装，15.2mm直径，带保护石英窗口，封装在黑色阳极氧化铝外壳中，52 × 52 mm²，配备DSUB9连接器' },
          { parameter: '测试条件', value: '室温23°C，偏置电压15V，光源波长940nm' }
        ]
      },
      {
        id: 21,
        categoryName: 'UV位置传感器',
        name: 'S2-0385-K',
        shortDescription: 'PSD带外壳UV探测器，4×4mm²有效区域，专业封装。',
        description: 'PSD带外壳UV探测器，4×4mm²有效区域，专业封装。',
        fullDescription: 'S2-0385-K是一款带外壳的PSD UV探测器，具有4×4mm²的有效区域。该产品采用专业封装设计，提供优异的性能和可靠性。',
        image: '/images/S2-0385-K.png',
        applicationDescription: '该探测器适用于需要高精度二维位置检测的UV光学系统，专业封装设计确保了长期稳定性和优异的环境适应性。',
        specifications: [
          { parameter: '有效区域', value: '4.0 × 4.0 mm²' },
          { parameter: '芯片区域', value: '6.4 × 6.4 mm²' },
          { parameter: '位置非线性', value: '1-2 %(±)' },
          { parameter: '探测器电阻', value: '7-16 kΩ' },
          { parameter: '漏电流', value: '50-200 nA' },
          { parameter: '噪声电流', value: '1.3-2.5 pA/√Hz' },
          { parameter: '响应度', value: '0.46 A/W' },
          { parameter: '电容', value: '20-25 pF' },
          { parameter: '上升时间(10%-90%)', value: '80-160 ns' },
          { parameter: '偏置电压(反向)', value: '5-30 V' },
          { parameter: '热漂移', value: '40-200 ppm/°C' },
          { parameter: '最大反向电压', value: '30 V' },
          { parameter: '工作温度', value: '70 °C' },
          { parameter: '存储温度', value: '100 °C' },
          { parameter: '封装', value: '4针PGA陶瓷封装，15.2mm直径，带保护石英窗口，封装在黑色阳极氧化铝外壳中，52 × 52 mm²，配备DSUB9连接器' },
          { parameter: '测试条件', value: '室温23°C，偏置电压15V，光源波长940nm' }
        ]
      },
      {
        id: 22,
        categoryName: 'UV位置传感器',
        name: 'PSD Holder MH01',
        shortDescription: 'PSD带外壳UV探测器，专业光学封装，高精度。',
        description: 'PSD带外壳UV探测器，专业光学封装，高精度。',
        fullDescription: 'PSD Holder MH01是一款专业的PSD带外壳UV探测器，采用专业光学封装设计，提供高精度和优异性能。该产品专为需要高精度位置检测的UV光学系统设计。',
        image: '/images/PSD Holder MH01 data sheet.png',
        applicationDescription: '该探测器适用于需要高精度位置检测的UV光学系统，专业光学封装设计确保了优异的光学性能和长期稳定性。',
        specifications: [
          { parameter: '封装类型', value: '专业光学封装' },
          { parameter: '精度等级', value: '高精度' },
          { parameter: '应用领域', value: 'UV光学系统' },
          { parameter: '性能特点', value: '优异的光学性能' },
          { parameter: '稳定性', value: '长期稳定' }
        ]
      },
      {
        id: 23,
        categoryName: 'UV位置传感器',
        name: 'SE-0018 SEEPOS',
        shortDescription: 'SE-0018系列产品，专业光学解决方案，高性能。',
        description: 'SE-0018系列产品，专业光学解决方案，高性能。',
        fullDescription: 'SE-0018 SEEPOS是一款专业的SE-0018系列产品，提供专业的光学解决方案和高性能。该产品专为需要高精度光学检测的系统设计。',
        image: '/images/SE-0018 SEEPOS.png',
        applicationDescription: '该产品适用于需要高精度光学检测的系统，专业的光学解决方案确保了优异的性能和可靠性。',
        specifications: [
          { parameter: '产品系列', value: 'SE-0018' },
          { parameter: '产品类型', value: '专业光学解决方案' },
          { parameter: '性能等级', value: '高性能' },
          { parameter: '应用领域', value: '高精度光学检测' },
          { parameter: '可靠性', value: '高可靠性' }
        ]
      },
      {
        id: 24,
        categoryName: 'UV位置传感器',
        name: 'SE-0011-D_D',
        shortDescription: '二位电路放大器，信号处理电路，厚膜技术。',
        description: '二位电路放大器，信号处理电路，厚膜技术。',
        fullDescription: 'SiTek SPC02是一款专门为SiTek PSD设计的信号处理电路。PSD电流作为双极电压输出，代表PSD上光斑质心的位置和强度。强度信号可用于外部归一化位置（差分）信号相对于光强度依赖性。',
        image: '/images/SE-0011-D_D_1.png',
        applicationDescription: '该电路适用于需要高精度信号处理的PSD系统，厚膜技术和激光修整电阻确保了最大精度、高可靠性和小尺寸。',
        specifications: [
          { parameter: '反向偏置', value: '15 V' },
          { parameter: '跨阻抗', value: '9.99×10⁴ - 10.01×10⁴ V/A' },
          { parameter: '求和和差分级放大', value: '0.999 - 1.001' },
          { parameter: '输出电压', value: '±12 V' },
          { parameter: '输出噪声', value: '3 mVp-p' },
          { parameter: '带宽', value: '400 kHz' },
          { parameter: '转换速率', value: '8-13 V/μs' },
          { parameter: '电源电流', value: '12-23 mA' },
          { parameter: '最大电源电压', value: '±18 V' },
          { parameter: '工作温度', value: '70 °C' },
          { parameter: '存储温度', value: '100 °C' },
          { parameter: '封装', value: '16针陶瓷基板，20.5 × 20.5 mm²，带DIL引脚' },
          { parameter: '测试条件', value: '室温23°C，电源电压±15V' }
        ]
      }
    ]

    const catalog = ref(fallbackProducts)

    const loadProduct = async (productId) => {
      const apiList = await fetchPublishedProducts()
      if (apiList.length) {
        catalog.value = mergeProducts(fallbackProducts, apiList)
      }
      const hardcoded = catalog.value.find(p => Number(p.id) === Number(productId))
      const apiProduct = await fetchProductById(productId)
      if (apiProduct) {
        const mapped = mapProduct(apiProduct)
        product.value = hardcoded ? {
          ...hardcoded,
          ...mapped,
          image: mapped.image || hardcoded.image,
          specifications: (mapped.specifications && mapped.specifications.length) ? mapped.specifications : hardcoded.specifications,
          features: (mapped.features && mapped.features.length) ? mapped.features : hardcoded.features,
          technicalDetails: mapped.technicalDetails || hardcoded.technicalDetails,
          kitInfo: mapped.kitInfo || hardcoded.kitInfo,
          kitImagePath: mapped.kitImagePath || hardcoded.kitImagePath,
          applications: mapped.applications || hardcoded.applications,
          disclaimer: mapped.disclaimer || hardcoded.disclaimer,
          dimensionImagePath: mapped.dimensionImagePath || hardcoded.dimensionImagePath,
          layout: mapped.layout || hardcoded.layout
        } : mapped
      } else {
        product.value = hardcoded || {}
      }
      relatedProducts.value = catalog.value
        .filter(p => Number(p.id) !== Number(productId))
        .slice(0, 3)
    }

    onMounted(() => {
      loadProduct(parseInt(route.params.id))
    })

    watch(() => route.params.id, (id) => {
      if (id) {
        loadProduct(parseInt(id))
      }
    })

    const formatSpecifications = (specs) => {
      return Array.isArray(specs) ? specs : [specs]
    }

    const navigateToProduct = (productId) => {
      router.push({
        name: 'ProductDetail',
        params: { id: productId.toString() }
      })
    }

    return {
      activeTab,
      product,
      relatedProducts,
      formatSpecifications,
      navigateToProduct,
      isMobile,
      specView,
      showPhotodiodeExtra
    }
  }
}
</script>

<style scoped>
.product-detail {
  padding: 40px;
  max-width: 1400px;
  margin: 0 auto;
}

.product-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-bottom: 60px;
}

.product-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.product-info h1 {
  font-size: 36px;
  color: #2c3e50;
  margin-bottom: 20px;
}

.description {
  font-size: 16px;
  line-height: 1.8;
  color: #4a5568;
}

.product-image {
  width: 100%;
  height: 400px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.product-content {
  margin-bottom: 60px;
}

/* 规格表样式 */
.specifications-section {
  padding: 20px 0;
}

/* 应用场景样式 */
.applications-section {
  padding: 20px 0;
}

.application-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.application-item {
  background: #f8fafc;
  padding: 30px;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.application-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.application-item h3 {
  font-size: 20px;
  color: #2c3e50;
  margin-bottom: 15px;
}

.application-item p {
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
  margin-bottom: 20px;
}

.application-image {
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
}

/* 相关产品推荐样式 */
.related-products {
  padding-top: 40px;
  border-top: 1px solid #e2e8f0;
}

.related-products h2 {
  font-size: 28px;
  color: #2c3e50;
  margin-bottom: 40px;
  text-align: center;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.product-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  position: relative;
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.product-card .product-image {
  height: 200px;
  transition: opacity 0.3s ease;
}

.product-card:hover .product-image {
  opacity: 0.95;
}

.product-card:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.product-card .product-info {
  padding: 20px;
}

.product-card h3 {
  font-size: 18px;
  color: #2c3e50;
  margin-bottom: 10px;
}

.product-card p {
  font-size: 14px;
  color: #4a5568;
  line-height: 1.6;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .product-detail {
    padding: 20px;
  }

  .product-header {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .product-image {
    height: 300px;
  }

  .product-info h1 {
    font-size: 28px;
  }

  .application-content {
    grid-template-columns: 1fr;
  }
}

/* 添加新的样式 */
.spec-description {
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  line-height: 1.8;
  color: #4a5568;
}

.application-text {
  padding: 30px;
  background: #f8fafc;
  border-radius: 12px;
  line-height: 1.8;
  color: #4a5568;
  transition: all 0.3s ease;
  font-size: 16px;
  white-space: pre-line;
}

.application-text:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

/* 图片预览相关样式 */
.product-image :deep(.el-image) {
  width: 100%;
  height: 100%;
  cursor: zoom-in;
}

.product-image :deep(.el-image:hover) {
  opacity: 0.9;
}

/* 修改和新增的样式 */
.content-section {
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.section-title {
  font-size: 24px;
  color: #2c3e50;
  margin-bottom: 30px;
  font-weight: 600;
  position: relative;
  padding-left: 15px;
}

.section-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 20px;
  background: #1890ff;
  border-radius: 2px;
}

.specifications-section {
  margin-bottom: 60px;
}

.applications-section {
  padding: 0;
}

.application-text {
  background: #f8fafc;
  padding: 30px;
  border-radius: 12px;
  line-height: 1.8;
  color: #4a5568;
  transition: all 0.3s ease;
  font-size: 16px;
}

.application-text:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 12px;
}

/* 移动端规格卡片样式 */
.mobile-specs-list {
  display: grid;
  gap: 20px;
}

.mobile-spec-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.mobile-spec-header {
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e2e8f0;
}

.mobile-spec-header h4 {
  font-size: 18px;
  color: #2c3e50;
  margin: 0;
  display: inline-block;
}

.spec-subtype {
  font-size: 14px;
  color: #4a5568;
  margin-left: 8px;
}

.mobile-spec-content {
  display: grid;
  grid-template-columns: 120px 1fr; /* 图片和详情左右布局 */
  gap: 20px;
  align-items: center;
}

.mobile-spec-image {
  border-radius: 4px;
  overflow: hidden;
  cursor: zoom-in;
}

.mobile-spec-details p {
  font-size: 14px;
  color: #4a5568;
  line-height: 1.7;
  margin: 5px 0;
}

.mobile-spec-details strong {
  color: #2c3e50;
  margin-right: 5px;
}

.kit-info-section {
  margin-top: 40px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  line-height: 1.8;
  color: #4a5568;
}

.kit-info-section h4 {
  font-size: 18px;
  color: #2c3e50;
  margin-bottom: 15px;
  font-weight: 600;
}

/* 新增产品详情页特定样式 */
.product-section {
  margin-bottom: 40px;
}

.sub-section-title {
  font-size: 20px;
  color: #2c3e50;
  margin-bottom: 20px;
  font-weight: 600;
  border-bottom: 2px solid #1890ff;
  padding-bottom: 8px;
  display: inline-block;
}

.features-section ul, .app-category ul {
  list-style: none;
  padding-left: 0;
}

.features-section li, .app-category li {
  margin-bottom: 10px;
  line-height: 1.7;
  color: #4a5568;
  position: relative;
  padding-left: 20px;
}

.features-section li::before, .app-category li::before {
  content: '■';
  position: absolute;
  left: 0;
  color: #1890ff; 
  font-size: 12px;
  top: 2px;
}

.applications-section-grid .app-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.app-category h4 {
  font-size: 16px;
  color: #096dd9;
  margin-bottom: 15px;
  font-weight: 600;
}

.spec-table-section .el-table {
  margin-bottom: 15px;
}

.spec-value-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.spec-value-list li {
  line-height: 1.6;
}

.disclaimer-text {
  font-size: 12px;
  color: #909399;
  margin-top: 15px;
  text-align: right;
}

.dimension-image-section .el-image {
   border-radius: 4px;
   box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
</style> 